import classNames from 'classnames'
import React from 'react'
import {Button} from 'antd'
import {IoCheckmarkCircle, IoTimeOutline} from 'react-icons/io5'
import {BiUnlink} from 'react-icons/bi'
import {HiOutlinePencilSquare} from 'react-icons/hi2'
import useTranslate from 'modules/local/useTranslate'

// Badge trạng thái tổng ("Chưa liên kết" / "Đang chờ duyệt" / "Đã xác thực (Active)" / "Bị từ chối")
// hiển thị cạnh tiêu đề trang - luôn cho biết ngay "đây là gì" mà không cần đọc hết nội dung bên dưới.
export const AssociationStatusBadge = ({status}) => {
    const t = useTranslate()
    const config = {
        empty: {className: 'bg-color-50 text-color-400 border-color-200', label: 'not linked yet'},
        pending: {className: 'bg-amber-50 text-amber-600 border-amber-300', label: 'pending approval'},
        connected: {className: 'bg-green-50 text-green-600 border-green-300', label: 'verified active'},
        rejected: {className: 'bg-red-50 text-red-500 border-red-300', label: 'request rejected'},
    }[status] || {}
    return (
        <span className={classNames(
            'inline-flex items-center gap-1 px-2.5 py-1 rounded-full border font-semibold text-xs whitespace-nowrap',
            config.className
        )}>
            {t(config.label)}
        </span>
    )
}

// Banner lớn phía trên - CHỈ hiện với pending/connected/rejected (trạng thái "chưa liên kết" thì
// wizard khai báo bên dưới đã đủ tự giải thích, không cần thêm 1 banner rỗng phía trên).
const AssociationStatusBanner = ({status, name, pathLabel, onEditRequest, isSubmitting}) => {
    const t = useTranslate()

    if (status === 'pending') {
        return (
            <div className="p-4 rounded-xl border border-amber-300 bg-amber-50 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex items-start gap-3 flex-1">
                    <IoTimeOutline size={26} className="text-amber-500 flex-shrink-0 mt-0.5"/>
                    <div className="flex-1 flex flex-col gap-1">
                        <div className="font-bold text-amber-700">
                            {t('verification request under review')}
                        </div>
                        <div className="text-sm text-amber-700 opacity-90 leading-relaxed">
                            {t('organization sent link request to path description', {
                                name: <strong>{name}</strong>,
                                path: pathLabel ? <strong>{pathLabel}</strong> : t('the selected level'),
                            })}
                        </div>
                    </div>
                </div>
                <Button
                    size="small"
                    loading={isSubmitting}
                    icon={<HiOutlinePencilSquare size={13} className="mr-1"/>}
                    className="self-start sm:self-auto button-rounded-md no-border bg-white flex-shrink-0"
                    onClick={onEditRequest}>
                    {t('edit / cancel request')}
                </Button>
            </div>
        )
    }

    if (status === 'connected') {
        return (
            <div className="p-4 rounded-xl border border-green-300 bg-green-50 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex items-start gap-3 flex-1">
                    <IoCheckmarkCircle size={26} className="text-green-500 flex-shrink-0 mt-0.5"/>
                    <div className="flex-1 flex flex-col gap-1">
                        <div className="font-bold text-green-700">
                            {t('organization has been officially verified')}
                        </div>
                        <div className="text-sm text-green-700 opacity-90 leading-relaxed">
                            {t('organization confirmed belongs to system of path description', {
                                name: <strong>{name}</strong>,
                                path: pathLabel ? <strong>{pathLabel}</strong> : t('the top level of the system'),
                            })}
                        </div>
                    </div>
                </div>
                {/* whitespace-nowrap: bắt buộc cho MỌI badge dạng span (xem docs/ui/design-principles.md).
                    style alignSelf inline (không phải class self-start/self-auto): span này là flex
                    item cạnh 1 sibling flex-1 cao (mô tả 2-3 dòng) - align-items mặc định của flex là
                    stretch, class self-start/sm:self-auto KHÔNG áp dụng nhất quán ở đây (đã thử, badge
                    vẫn bị kéo cao theo sibling ra hình oval rỗng) - antd Button ở 2 banner còn lại
                    không bị vì Button tự có height cố định riêng, span thường thì không. Set thẳng
                    inline style để chắc chắn override, không phụ thuộc thứ tự/purge của Tailwind. */}
                <span
                    style={{alignSelf: 'flex-start'}}
                    className="flex-shrink-0 whitespace-nowrap px-2.5 py-1 rounded-full border border-green-400 bg-white text-green-600 font-bold text-xs">
                    {t('verified official')}
                </span>
            </div>
        )
    }

    if (status === 'rejected') {
        return (
            <div className="p-4 rounded-xl border border-red-300 bg-red-50 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex items-start gap-3 flex-1">
                    <BiUnlink size={24} className="text-red-500 flex-shrink-0 mt-0.5"/>
                    <div className="flex-1 flex flex-col gap-1">
                        <div className="font-bold text-red-600">
                            {t('link request was rejected')}
                        </div>
                        <div className="text-sm text-red-500 opacity-90 leading-relaxed">
                            {t('link request was rejected description')}
                        </div>
                    </div>
                </div>
                <Button
                    size="small"
                    loading={isSubmitting}
                    className="self-start sm:self-auto button-rounded-md no-border bg-white flex-shrink-0"
                    onClick={onEditRequest}>
                    {t('declare again')}
                </Button>
            </div>
        )
    }

    return null
}

export const AssociationChangeRequestButton = ({loading, onClick}) => {
    const t = useTranslate()
    return (
        <Button
            size="small"
            loading={loading}
            icon={<HiOutlinePencilSquare size={13} className="mr-1"/>}
            className="button-rounded-md no-border background-100"
            onClick={onClick}>
            {t('change / request system tree transfer')}
        </Button>
    )
}

export default AssociationStatusBanner
